import dayjs from "dayjs";

import { serverResponseFormat } from "@similarweb/pro-logic-components";

import { swSettings } from "app/common/services/swSettings";
import { EErrorTypes } from "app/components/React/empty-state/GenericEmptyState";
import { CHART_COLORS } from "app/constants/ChartColors";
import { NO_DATA_SIGN } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { COLUMN_KEYS } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/consts";
import type { Prompt } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/types";
import type {
    ConfigurationsDict,
    ExplicitLLModel,
    IAiTrackerCampaign,
    ICampaignConfig,
    ILLMWidgetRequestParams,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import {
    CampaignStatus,
    CitationGapStatus,
    EAvailableTabs,
    LLModel,
    SentimentStatus,
    VolatilityStatus,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import type { DatesRange } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/types/common";
import { granularityNameToMomentUnit } from "app/UtilitiesAndConstants/Constants/Moment";
import type { IWebsiteItem } from "pro-features/components/AutocompleteWebsites/types";
import { ROUTES as RECOMMENDATIONS_ROUTES } from "single-spa/react-app/config/solutions25/genAIIntelligence/Recommendations/RecommendationsConstants";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { FilterOption } from "./filterTypes";
import {
    AI_MODELS_SORT_ORDER,
    AI_TRACKER_DEFAULT_DURATION,
    ALL_MODELS_NAME,
    ALL_TOPICS_NAME,
    BLANK_LEGEND_COLOR,
    DEFAULT_LLM,
    getAvailableTabsConfig,
    getSentimentStatusConfig,
    MAX_DEFAULT_CAMPAIGN_DURATION,
} from "./LLMConsts";

export const hasNonDemoCampaigns = (campaigns: IAiTrackerCampaign[]) => campaigns.some((c) => !c.isDemo);

const SENTIMENT_PRIORITY_MAP: Record<SentimentStatus, number> = {
    [SentimentStatus.Positive]: 1,
    [SentimentStatus.Neutral]: 2,
    [SentimentStatus.Negative]: 3,
    [SentimentStatus.NotMentioned]: 4,
};

const SENTIMENT_SORT_PRIORITY_MAP: Partial<Record<SentimentStatus, number>> = {
    [SentimentStatus.Positive]: 1,
    [SentimentStatus.Neutral]: 2,
    [SentimentStatus.Negative]: 3,
};

const VOLATILITY_PRIORITY_MAP: Record<VolatilityStatus, number> = {
    [VolatilityStatus.Stable]: 1,
    [VolatilityStatus.Moderate]: 2,
    [VolatilityStatus.Volatile]: 3,
};

const CITATION_GAP_PRIORITY_MAP: Record<CitationGapStatus, number> = {
    [CitationGapStatus.None]: 1,
    [CitationGapStatus.Partial]: 2,
    [CitationGapStatus.Full]: 3,
};

const getSentimentPriority = (status: SentimentStatus): number => {
    return SENTIMENT_PRIORITY_MAP[status];
};

const compareSentimentStatuses = (statusA: SentimentStatus, statusB: SentimentStatus): number => {
    const priorityA = getSentimentPriority(statusA);
    const priorityB = getSentimentPriority(statusB);

    return priorityA - priorityB;
};

export const getWidgetNoDataProps = (showNoData: boolean, isError: boolean) => ({
    noData: showNoData,
    noDataTitle: isError ? translate("common.error") : translate("common.noresults"),
    noDataSubtitle: isError ? translate("common.error.subtitle") : translate("common.noresults.subtitle"),
    noDataType: isError ? EErrorTypes.ERROR : EErrorTypes.NO_DATA,
});

/**
 * Dynamically assigns colors to legend items based on visibility state.
 * This ensures color stability when users toggle legend items on/off,
 * preventing jarring color changes across the chart.
 */
export const assignDynamicLegendColors = <
    T extends {
        visible: boolean;
        color: string;
    },
>(
    items: T[],
): T[] => {
    const colorPalette = CHART_COLORS.chartMainColors;
    const usedColors = new Set<string>();
    const reservedFirstColor = colorPalette[0];
    usedColors.add(reservedFirstColor);

    //mark colors that are already assigned to selected items (except first item)
    items.forEach((item, index) => {
        if (
            index !== 0 &&
            item.visible &&
            item.color &&
            item.color !== BLANK_LEGEND_COLOR &&
            colorPalette.includes(item.color)
        ) {
            usedColors.add(item.color);
        }
    });

    const availableColors = colorPalette.filter((color) => !usedColors.has(color));
    let availableColorIndex = 0;

    return items.map((item, index) => {
        if (!item.visible) {
            return { ...item, color: BLANK_LEGEND_COLOR };
        }

        if (index === 0) {
            return { ...item, color: reservedFirstColor };
        }

        //keep the color if it's valid
        if (
            item.color &&
            item.color !== BLANK_LEGEND_COLOR &&
            item.color !== reservedFirstColor &&
            colorPalette.includes(item.color)
        ) {
            return item;
        }

        const nextAvailableColor = availableColors[availableColorIndex++ % availableColors.length];
        return { ...item, color: nextAvailableColor };
    });
};

// Pushes empty/unmapped values to the bottom regardless of sort direction.
// The table negates the comparator result for descending, so we counteract
// that by flipping the sign for empty-vs-non-empty comparisons in descending.
export const emptyLastModifier = (sortOrder: string | null | undefined) => (sortOrder === "descend" ? -1 : 1);

const statusPrioritySorter = <T extends string>(
    a: Prompt,
    b: Prompt,
    sortKey: keyof Prompt,
    priorityMap: Partial<Record<T, number>>,
    sortOrder?: string | null,
) => {
    const statusA = a[sortKey] as T | null | undefined;
    const statusB = b[sortKey] as T | null | undefined;

    const priorityA = statusA ? priorityMap[statusA] : undefined;
    const priorityB = statusB ? priorityMap[statusB] : undefined;

    const hasA = priorityA != null;
    const hasB = priorityB != null;

    if (hasA && hasB) {
        return priorityA - priorityB;
    }
    if (!hasA && !hasB) {
        return 0;
    }

    return (hasA ? -1 : 1) * emptyLastModifier(sortOrder);
};

export const sentimentStatusSorter = (a: Prompt, b: Prompt, sortKey: keyof Prompt, sortOrder?: string | null) =>
    statusPrioritySorter(a, b, sortKey, SENTIMENT_SORT_PRIORITY_MAP, sortOrder);

export const volatilityStatusSorter = (a: Prompt, b: Prompt, sortOrder?: string | null) =>
    statusPrioritySorter(a, b, COLUMN_KEYS.citationVolatility, VOLATILITY_PRIORITY_MAP, sortOrder);

export const citationGapStatusSorter = (a: Prompt, b: Prompt, sortOrder?: string | null) =>
    statusPrioritySorter(a, b, COLUMN_KEYS.citationGap, CITATION_GAP_PRIORITY_MAP, sortOrder);

export const formatAveragePosition = (averagePosition: number | null | undefined): string =>
    averagePosition == null || Number.isNaN(averagePosition) ? NO_DATA_SIGN : averagePosition.toFixed(1);

export const getTopicOptions = <T extends { topics?: string[] }>(data: T[]): FilterOption[] => {
    const topicCounts = new Map<string, number>();

    data?.forEach((item) => {
        item?.topics?.forEach((topic) => {
            if (topic) {
                topicCounts.set(topic, (topicCounts.get(topic) || 0) + 1);
            }
        });
    });

    const topTopics = Array.from(topicCounts.entries())
        .sort(([, a], [, b]) => b - a)
        .map(([topic, count]) => ({
            label: `${topic} (${count})`,
            value: topic,
        }));

    return topTopics;
};

export const getSentimentStatusOptions = <T extends { mentionSentimentStatus?: SentimentStatus }>(
    data: T[],
): FilterOption[] => {
    const sentimentCounts = new Map<SentimentStatus, number>();

    data?.forEach((item) => {
        const status =
            "mentionSentimentStatus" in item ? item.mentionSentimentStatus : (item as any).mentionSentimentStatus;
        if (status) {
            sentimentCounts.set(status, (sentimentCounts.get(status) || 0) + 1);
        }
    });

    const sentimentStatuses: FilterOption[] = [];

    for (const [status, count] of sentimentCounts.entries()) {
        const validStatus = status?.toLowerCase();
        const statusConfig = getSentimentStatusConfig()?.[validStatus];
        sentimentStatuses.push({
            label: `${statusConfig?.text || validStatus} (${count})`,
            value: validStatus,
        });
    }

    sentimentStatuses.sort((a, b) => {
        const statusA = a.value as SentimentStatus;
        const statusB = b.value as SentimentStatus;

        return compareSentimentStatuses(statusA, statusB);
    });

    return sentimentStatuses;
};

export const getBrandOptions = <T extends { brands?: string[] }>(data: T[]): FilterOption[] => {
    const brandsCounts = new Map<string, number>();

    data?.forEach((item) => {
        item?.brands?.forEach((brand) => {
            if (brand) {
                brandsCounts.set(brand, (brandsCounts.get(brand) || 0) + 1);
            }
        });
    });

    const topBrands = Array.from(brandsCounts.entries())
        .sort(([, a], [, b]) => b - a)
        .map(([brand]) => ({
            label: brand,
            value: brand,
        }));

    return topBrands;
};

export const getBaseWidgetRequestParams = (
    campaign: IAiTrackerCampaign,
    campaignConfig: ICampaignConfig,
    reportDuration: DatesRange,
): ILLMWidgetRequestParams => ({
    campaignGuid: campaign?.id,
    scrapingConfigurationGuids: [campaignConfig?.configId],
    startDate: reportDuration.from.format(serverResponseFormat),
    endDate: reportDuration.to.format(serverResponseFormat),
});

export const hasPreviousPeriodWindowForModel = (
    reportDuration: DatesRange,
    campaignConfig?: ICampaignConfig,
): boolean => {
    if (!reportDuration?.from || !reportDuration?.to || !campaignConfig?.firstScrapeDate) {
        return false;
    }

    const firstScrapeDate = dayjs.utc(campaignConfig.firstScrapeDate).startOf("day");
    const periodStart = dayjs.utc(reportDuration.from.format(serverResponseFormat)).startOf("day");
    const periodEnd = dayjs.utc(reportDuration.to.format(serverResponseFormat)).endOf("day");

    const reportDays = periodEnd.diff(periodStart, "days") + 1;
    const daysBeforeCurrentPeriod = periodStart.diff(firstScrapeDate, "days");

    return daysBeforeCurrentPeriod >= reportDays;
};

export const hasPreviousPeriodWindow = (reportDuration: DatesRange, campaign?: IAiTrackerCampaign): boolean =>
    Object.values(campaign?.configs ?? {}).some((config) => hasPreviousPeriodWindowForModel(reportDuration, config));

export const getTopicsFilterParam = (topic: string | undefined): string[] | undefined =>
    topic && topic !== ALL_TOPICS_NAME ? [topic] : undefined;

export const sortByMyBrandFirst = <T extends { isMyBrand?: boolean; total: number }>(items: T[]): T[] =>
    [...items].sort((a, b) => {
        if (a.isMyBrand && !b.isMyBrand) {
            return -1;
        }
        if (!a.isMyBrand && b.isMyBrand) {
            return 1;
        }
        return b.total - a.total;
    });

export const filterOpenModelsForUser = (models: LLModel[], isDemo: boolean = false): LLModel[] => {
    if (!models?.some(Boolean)) {
        return [];
    }

    const claimValue: string[] = (swSettings.components?.AiTracker?.resources?.Campaignsllms as string[]) || [];

    if (isDemo || claimValue.includes(ALL_MODELS_NAME)) {
        return models;
    }

    return models.filter(
        (model) =>
            model && (model === LLModel.All || claimValue.some((claim) => claim.toLowerCase() === model.toLowerCase())),
    );
};

export const isModelOpenForUser = (model: LLModel, isDemo: boolean = false): boolean =>
    !!filterOpenModelsForUser([model], isDemo)?.length;

export const isAllPlatformsOptionEnabled = (currentTab: EAvailableTabs): boolean => {
    return currentTab === EAvailableTabs.BRANDS;
};

export const sortModels = (models: LLModel[], defaultModel: LLModel): LLModel[] => {
    return models.toSorted((modelA, modelB) => {
        if (modelA === defaultModel) {
            return -1;
        } else if (modelB === defaultModel) {
            return 1;
        }

        return AI_MODELS_SORT_ORDER.indexOf(modelA) - AI_MODELS_SORT_ORDER.indexOf(modelB);
    });
};

export const getDefaultModel = (
    campaign: { configs: ConfigurationsDict; isDemo: boolean },
    currentTab?: EAvailableTabs,
    completeConfigOnly?: boolean,
): LLModel => {
    const { configs, isDemo } = campaign;
    const campaignModels = Object.keys(configs) as LLModel[];
    const filteredModelsForUser = filterOpenModelsForUser(campaignModels, isDemo).filter(
        (model) => model !== LLModel.All,
    );
    if (!filteredModelsForUser?.length) {
        return undefined;
    }

    if (currentTab && isAllPlatformsOptionEnabled(currentTab) && configs?.[LLModel.All]) {
        return LLModel.All;
    }

    if (
        isModelOpenForUser(DEFAULT_LLM, isDemo) &&
        (!completeConfigOnly || configs?.[DEFAULT_LLM]?.status === CampaignStatus.Completed)
    ) {
        return DEFAULT_LLM;
    }

    //find first complete config
    const sortedModels = sortModels(filteredModelsForUser, DEFAULT_LLM);
    const anyCompleteConfig = sortedModels?.find((model) => configs[model]?.status === CampaignStatus.Completed);

    return anyCompleteConfig ?? DEFAULT_LLM;
};

export const getMaxCampaigns = (): number =>
    swSettings.user.isInternalUser
        ? swSettings.components.AiTracker.resources.MaxCampaignsInternalAIBrandTracker
        : swSettings.components.AiTracker.resources.AIBrandTrackerCampaigns;

export const extractConfigScrapeDuration = (campaign: IAiTrackerCampaign, model: LLModel): DatesRange => {
    const config = campaign.configs?.[model];

    return {
        from: dayjs.utc(config?.firstScrapeDate).startOf("day"),
        to: dayjs.utc(config?.lastScrapeDate).endOf("day"),
    };
};

export const calcCampaignEntireDuration = (campaign: IAiTrackerCampaign, model: LLModel): string => {
    const dailyDuration = extractConfigScrapeDuration(campaign, model);
    const dayDiffCampaignCreation = dailyDuration.to.diff(dailyDuration.from, granularityNameToMomentUnit.Daily);
    return dayDiffCampaignCreation > MAX_DEFAULT_CAMPAIGN_DURATION
        ? AI_TRACKER_DEFAULT_DURATION
        : `${dayDiffCampaignCreation}d`;
};

export const getMaxPrompts = (): number =>
    (swSettings.components?.AiTracker?.resources?.CampaignsPrompts ?? 0) as number;

type NavigateToReportOptions = {
    preserveCurrentDuration?: boolean;
};

export const navigateToReport = (
    campaign: IAiTrackerCampaign,
    model: LLModel = undefined,
    tab: EAvailableTabs = EAvailableTabs.BRANDS,
    params: Record<string, unknown> = {},
    options: NavigateToReportOptions = {},
) => {
    if (!campaign) {
        return;
    }

    const { preserveCurrentDuration = true } = options;

    const resolvedModel =
        model && isModelOpenForUser(model, campaign.isDemo) && model in campaign.configs
            ? model
            : getDefaultModel(campaign, tab);
    const tabConfig = getAvailableTabsConfig()[tab];
    const currentParams = proNavigator.getParams();
    const isCampaignChange = currentParams?.campaignId !== campaign.id;
    const currentDuration = currentParams?.duration as string | undefined;

    void proNavigator.go(tabConfig.stateId, {
        campaignId: campaign.id,
        model: resolvedModel,
        duration: (preserveCurrentDuration && currentDuration) || calcCampaignEntireDuration(campaign, resolvedModel),
        isDurationChanged: !isCampaignChange && currentParams?.isDurationChanged,
        topic: isCampaignChange ? ALL_TOPICS_NAME : currentParams?.topic,
        domainFilter: isCampaignChange ? undefined : currentParams?.domainFilter,
        ...params,
    });
};

const getConfigStatuses = (campaign?: IAiTrackerCampaign): CampaignStatus[] =>
    Object.values(campaign?.configs ?? {}).map((config) => config.status);

export const isCampaignCompleted = (campaign?: IAiTrackerCampaign): boolean => {
    const statuses = getConfigStatuses(campaign);
    return statuses.every((status) => status === CampaignStatus.Completed);
};

export const isCampaignFailed = (campaign?: IAiTrackerCampaign): boolean => {
    const statuses = getConfigStatuses(campaign);
    return statuses.every((status) => status === CampaignStatus.Failed);
};

export const areAllModelsCompleted = (campaign?: IAiTrackerCampaign): boolean => {
    const statuses = getConfigStatuses(campaign);
    return statuses.length > 0 && statuses.every((status) => status === CampaignStatus.Completed);
};

export const getDuplicateDomainNameIndices = (names: string[]): Set<number> => {
    const uniqueDomainNames = new Set<string>();
    const duplicateIndices = new Set<number>();

    names?.forEach((name, index) => {
        const domainName = name?.trim().toLowerCase() ?? "";
        if (!domainName) {
            return;
        }

        if (uniqueDomainNames.has(domainName)) {
            duplicateIndices.add(index);
        } else {
            uniqueDomainNames.add(domainName);
        }
    });

    return duplicateIndices;
};

export const getDuplicateTrackedDomainIndices = (domains: IWebsiteItem[]): Set<number> =>
    getDuplicateDomainNameIndices(domains.map(({ name }) => name));

export const navigateToRecommendations = (campaign: IAiTrackerCampaign, params: Record<string, unknown> = {}) => {
    if (!campaign) {
        return;
    }

    void proNavigator.go(RECOMMENDATIONS_ROUTES.overview, {
        campaignId: campaign.id,
        model: DEFAULT_LLM,
        isDurationChanged: false,
        ...params,
    });
};

export const getCampaignModels = (configs?: ConfigurationsDict): ExplicitLLModel[] =>
    Object.keys(configs ?? {}).filter((model) => (model as LLModel) !== LLModel.All) as ExplicitLLModel[];
